import React, { useState, useContext, useEffect } from "react";
import {
  LoadingOverlay,
} from "@mantine/core";
import { IconPhoneCheck } from "@tabler/icons-react";
import { DataContext } from "../../context/DataContext";
import DataCollectionForm from "../../components/data-collection-form";
export default function PhoneMatch() {
  const { text, settings } = useContext(DataContext);
  const currentPath = "phonematch";
  const [formConfig, setFormConfig] = useState(null);
  const [reattempts, setReattempts] = useState(1);

  useEffect(() => {
    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath,
    );

    if (matchedRoute) {
      setReattempts(matchedRoute.reattempts || 1);
    }

    const inputs = (matchedRoute?.inputs || []).map((input) =>
      typeof input === "string" ? input.toLowerCase() : input,
    );

    const needsAddress = inputs.includes("address");
    const needsSsn = inputs.includes("ssn");

    // Step 0: Name fields
    const nameFields = [
      {
        name: "fn",
        label: text?.general?.firstName || "First Name",
        group: "name",
        validation: (value) =>
          value.length < 2
            ? text?.general?.firstNameError || "First name is too short"
            : null,
      },
      {
        name: "ln",
        label: text?.general?.lastName || "Last Name",
        group: "name",
        validation: (value) =>
          value.length < 2
            ? text?.general?.lastNameError || "Last name is too short"
            : null,
      },
    ];

    // Step 1: Phone and DOB
    const phoneAndDobFields = [
      {
        name: "phone",
        label: text?.general?.phone || "Phone Number",
        group: "phone_dob",
        validation: (value) =>
          value.replace(/\D/g, "").length !== 10
            ? text?.general?.phoneError || "Phone number is invalid"
            : null,
      },
      {
        name: "dob",
        label: text?.general?.DateOfBirth || "Date of Birth",
        type: "date",
        group: "phone_dob",
        validation: (value) => {
          const dobPattern =
            /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
          const isDobFormatCorrect = dobPattern.test(value);

          let dobError =
            text?.general?.DateOfBirthError || "Invalid Date of Birth";

          if (isDobFormatCorrect) {
            const year = parseInt(value.substring(0, 4), 10);
            const month = parseInt(value.substring(4, 6), 10) - 1;
            const day = parseInt(value.substring(6, 8), 10);

            const date = new Date(year, month, day);

            if (
              date.getFullYear() === year &&
              date.getMonth() === month &&
              date.getDate() === day
            ) {
              dobError = null;
            }
          }
          return dobError;
        },
      },
    ];

    let addressFields = [];

    // Step 2: Address (only if needed)
    if (needsAddress) {
      const isUKAddressSetting =
        settings?.journey?.settings?.isUKAddress ||
        settings?.journey?.settings?.isUkAddress ||
        matchedRoute?.settings?.isUKAddress ||
        matchedRoute?.settings?.isUkAddress ||
        matchedRoute?.isUKAddress ||
        matchedRoute?.isUkAddress;

      addressFields = [
        {
          name: "address",
          label: text?.general?.address || "Address",
          type: "address",
          group: "address",
          validation: (value) => {
            let addrError = "Address is required";

            if (isUKAddressSetting) {
              const ukPostcodePattern =
                /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
              if (
                value.addr &&
                value.city &&
                value.state &&
                ukPostcodePattern.test(value.zip.trim())
              ) {
                addrError = null;
              }
            } else if (
              value.addr &&
              value.city &&
              value.state &&
              value.zip.trim().length === 5
            ) {
              addrError = null;
            }

            return addrError;
          },
        },
      ];
    }

    // Add SSN to appropriate step
    if (needsSsn) {
      const ssnField = {
        name: "ssn",
        label: text?.general?.lastFourSSN || "Last 4 digits of SSN",
        group: needsAddress ? "address" : "phone_dob", // Add to address step if address exists, otherwise phone_dob step
        isMaskedInput: true,
        maxLength: 4,
        validation: (value) => {
          if (/[^0-9]/.test(value) || value.length !== 4) {
            return text?.general?.lastFourSSNError || "Invalid SSN";
          }
          return null;
        },
      };

      if (needsAddress) {
        addressFields.push(ssnField);
      } else {
        phoneAndDobFields.push(ssnField);
      }
    }

    setFormConfig({
      fields: [...nameFields, ...phoneAndDobFields, ...addressFields],
      hasAddress: needsAddress
    });
  }, [text, settings]);

  if (!formConfig) {
    return <LoadingOverlay visible />;
  }

  return (
    <DataCollectionForm
      config={formConfig}
      title={text?.phonematch?.title || "Verify your Phone"}
      subtitle={
        text?.phonematch?.subTitle ||
        "Please verify your phone number by entering your details."
      }
      buttonText={text?.phonematch?.button || "Verify My Phone"}
      icon={
        <IconPhoneCheck
          size={40}
          stroke={1.5}
        />
      }
      componentKey={currentPath}
      endpoint="/v3/stable/api/frontend/phonematch"
      reAttempts={reattempts}
    />
  );
}
